// VideoUpload component for merchant product creation
// Supports both file upload and URL input modes

import React, { useState, useCallback, useRef } from 'react'
import type { VideoSource } from '../../../../app/modules/merchant-product/types/FileUpload'

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ')
}

export interface VideoUploadProps {
  /** Current video value */
  value: VideoSource | null
  /** Change handler when video is modified */
  onChange: (video: VideoSource | null) => void
  /** File upload handler for server-side file management */
  onFileUpload?: (file: File) => Promise<{ file_id: string; file_url: string }>
  /** Maximum file size in MB */
  maxFileSize?: number
  /** Validation error */
  error?: string
  /** Whether the component is disabled */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

const VideoUpload: React.FC<VideoUploadProps> = ({
  value,
  onChange,
  onFileUpload,
  maxFileSize = 100, // Default 100MB for videos
  error,
  disabled = false,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState<'file' | 'url'>(value?.type || 'file')
  const [isDragOver, setIsDragOver] = useState(false)
  const [urlInput, setUrlInput] = useState(value?.type === 'url' ? value.url || '' : '')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Accepted video formats
  const acceptedFormats = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm']
  const acceptedExtensions = ['.mp4', '.mov', '.avi', '.webm']

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize * 1024 * 1024) {
      return `Video file is too large. Maximum size is ${maxFileSize}MB`
    }

    if (!file.type.startsWith('video/')) {
      return `File is not a valid video`
    }

    if (!acceptedFormats.includes(file.type)) {
      return `Video format not supported. Use MP4, MOV, AVI, or WEBM`
    }

    return null
  }

  // Handle file selection
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const file = files[0]

    // Validate file
    const validationError = validateFile(file)
    if (validationError) {
      onChange({
        type: 'file',
        file,
        upload_status: 'error',
        error_message: validationError,
        preview_url: URL.createObjectURL(file)
      })
      return
    }

    // Create preview immediately
    const preview_url = URL.createObjectURL(file)

    if (onFileUpload) {
      // Upload to server
      onChange({
        type: 'file',
        file,
        upload_status: 'pending',
        preview_url
      })

      try {
        // Set uploading status
        onChange({
          type: 'file',
          file,
          upload_status: 'uploading',
          preview_url
        })

        // Upload to server
        const result = await onFileUpload(file)

        // Update with success
        onChange({
          type: 'file',
          file,
          file_id: result.file_id,
          url: result.file_url,
          upload_status: 'success',
          preview_url
        })
      } catch (error: any) {
        // Update with error
        onChange({
          type: 'file',
          file,
          upload_status: 'error',
          error_message: error?.message || 'Upload failed',
          preview_url
        })
      }
    } else {
      // Local mode without upload
      onChange({
        type: 'file',
        file,
        preview_url
      })
    }
  }, [onFileUpload, onChange, maxFileSize])

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files)
    e.target.value = '' // Reset input
  }

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragOver(true)
    }
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (!disabled) {
      handleFileSelect(e.dataTransfer.files)
    }
  }, [disabled, handleFileSelect])

  // Trigger file input
  const triggerFileInput = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click()
    }
  }

  // Handle URL input
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value
    setUrlInput(url)
  }

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return

    onChange({
      type: 'url',
      url: urlInput.trim()
    })
  }

  // Remove video
  const handleRemove = () => {
    onChange(null)
    setUrlInput('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Retry failed upload
  const handleRetry = async () => {
    if (!value || value.type !== 'file' || !value.file || !onFileUpload) return

    onChange({
      ...value,
      upload_status: 'uploading',
      error_message: undefined
    })

    try {
      const result = await onFileUpload(value.file)

      onChange({
        ...value,
        file_id: result.file_id,
        url: result.file_url,
        upload_status: 'success',
        error_message: undefined
      })
    } catch (error: any) {
      onChange({
        ...value,
        upload_status: 'error',
        error_message: error?.message || 'Upload failed'
      })
    }
  }

  return (
    <div className={classNames('tw-w-full tw-flex tw-flex-col tw-gap-3', className)}>
      {/* Tab Navigation */}
      <div className="tw-flex tw-gap-2 tw-border-b tw-border-gray-200 dark:tw-border-gray-700">
        <button
          type="button"
          onClick={() => setActiveTab('file')}
          disabled={disabled}
          className={classNames(
            'tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors tw-border-b-2',
            activeTab === 'file'
              ? 'tw-border-primary tw-text-primary'
              : 'tw-border-transparent tw-text-gray-500 dark:tw-text-gray-400 hover:tw-text-gray-700 dark:hover:tw-text-gray-300',
            disabled && 'tw-opacity-50 tw-cursor-not-allowed'
          )}
        >
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('url')}
          disabled={disabled}
          className={classNames(
            'tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors tw-border-b-2',
            activeTab === 'url'
              ? 'tw-border-primary tw-text-primary'
              : 'tw-border-transparent tw-text-gray-500 dark:tw-text-gray-400 hover:tw-text-gray-700 dark:hover:tw-text-gray-300',
            disabled && 'tw-opacity-50 tw-cursor-not-allowed'
          )}
        >
          Video URL
        </button>
      </div>

      {/* File Upload Tab */}
      {activeTab === 'file' && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {!value || value.type !== 'file' ? (
            // Upload zone
            <div
              className={classNames(
                'tw-border-2 tw-border-dashed tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-transition-all tw-cursor-pointer',
                isDragOver
                  ? 'tw-border-primary tw-bg-primary/5 dark:tw-bg-primary/10'
                  : 'tw-border-gray-300 dark:tw-border-gray-600',
                disabled
                  ? 'tw-bg-gray-50 dark:tw-bg-gray-800 tw-cursor-not-allowed tw-opacity-50'
                  : 'hover:tw-border-primary hover:tw-bg-primary/5 dark:hover:tw-bg-primary/10'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedExtensions.join(',')}
                onChange={handleInputChange}
                disabled={disabled}
                className="tw-hidden"
                aria-label="Upload product video"
              />

              <span className="material-symbols-outlined tw-text-4xl tw-text-gray-400">
                video_file
              </span>
              <div className="tw-text-center">
                <p className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
                  Drop video file here or click to browse
                </p>
                <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
                  MP4, MOV, AVI, WEBM - Max {maxFileSize}MB
                </p>
              </div>
            </div>
          ) : (
            // Video preview
            <div className={classNames(
              'tw-border tw-rounded-lg tw-p-4 tw-bg-gray-50 dark:tw-bg-gray-800',
              value.upload_status === 'error' && 'tw-border-red-300 dark:tw-border-red-600'
            )}>
              <div className="tw-flex tw-items-start tw-gap-4">
                {/* Video preview */}
                <div className="tw-flex-shrink-0 tw-w-32 tw-h-32 tw-rounded-lg tw-bg-black tw-overflow-hidden tw-relative">
                  {value.preview_url && (
                    <video
                      src={value.preview_url}
                      className="tw-w-full tw-h-full tw-object-cover"
                      controls={false}
                    />
                  )}

                  {/* Upload status overlay */}
                  {value.upload_status === 'uploading' && (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
                      <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-4 tw-border-white tw-border-t-transparent" />
                    </div>
                  )}

                  {value.upload_status === 'success' && (
                    <div className="tw-absolute tw-top-2 tw-right-2 tw-bg-green-500 tw-text-white tw-p-1 tw-rounded-full">
                      <span className="material-symbols-outlined tw-text-[14px]">check_circle</span>
                    </div>
                  )}

                  {value.upload_status === 'error' && (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-red-500/20">
                      <span className="material-symbols-outlined tw-text-red-500 tw-text-3xl">error</span>
                    </div>
                  )}
                </div>

                {/* Video info */}
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white tw-truncate">
                    {value.file?.name || 'Video file'}
                  </p>
                  {value.file && (
                    <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
                      {(value.file.size / 1024 / 1024).toFixed(1)} MB
                      {value.upload_status === 'uploading' && (
                        <> • <span className="tw-text-blue-600 dark:tw-text-blue-400">Uploading...</span></>
                      )}
                      {value.upload_status === 'success' && (
                        <> • <span className="tw-text-green-600 dark:tw-text-green-400">Uploaded</span></>
                      )}
                    </p>
                  )}

                  {/* Error message */}
                  {value.upload_status === 'error' && value.error_message && (
                    <p className="tw-text-xs tw-text-red-600 dark:tw-text-red-400 tw-mt-2">
                      {value.error_message}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="tw-flex tw-gap-2 tw-mt-3">
                    {value.upload_status === 'error' && (
                      <button
                        type="button"
                        onClick={handleRetry}
                        className="tw-px-3 tw-py-1.5 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors"
                      >
                        Retry Upload
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleRemove}
                      className="tw-px-3 tw-py-1.5 tw-bg-gray-200 dark:tw-bg-gray-700 hover:tw-bg-gray-300 dark:hover:tw-bg-gray-600 tw-text-gray-700 dark:tw-text-gray-300 tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* URL Input Tab */}
      {activeTab === 'url' && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {!value || value.type !== 'url' ? (
            // URL input
            <div className="tw-flex tw-flex-col tw-gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={handleUrlChange}
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                disabled={disabled}
                className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
              />
              <button
                type="button"
                onClick={handleUrlSubmit}
                disabled={!urlInput.trim() || disabled}
                className="tw-px-4 tw-py-2 tw-bg-primary hover:tw-bg-primary/90 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Add Video URL
              </button>
              <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                Supports YouTube, Vimeo, or direct video URLs
              </p>
            </div>
          ) : (
            // URL preview
            <div className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-p-4 tw-bg-gray-50 dark:tw-bg-gray-800">
              <div className="tw-flex tw-items-start tw-gap-3">
                <span className="material-symbols-outlined tw-text-2xl tw-text-primary">
                  link
                </span>
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white">
                    Video URL
                  </p>
                  <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1 tw-truncate">
                    {value.url}
                  </p>
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="tw-mt-2 tw-px-3 tw-py-1.5 tw-bg-gray-200 dark:tw-bg-gray-700 hover:tw-bg-gray-300 dark:hover:tw-bg-gray-600 tw-text-gray-700 dark:tw-text-gray-300 tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="tw-p-3 tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-red-600 dark:tw-text-red-400">
            <span className="material-symbols-outlined tw-text-[16px]">error</span>
            <span className="tw-text-xs tw-font-medium">{error}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoUpload
